import {
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { useMemo } from "react";
import type { OpenOrder } from "#/hooks/use-hyperliquid-account";
import { POSITIONS_GRID_CLASS } from "./constants/positions.constants.ts";
import { createPositionsColumns } from "./positions-columns.tsx";
import type {
	PositionRowItem,
	PositionsTableMeta,
} from "./types/positions.types.ts";
import { VirtualizedGridTable } from "./virtualized-grid-table.tsx";

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();

type PositionsTableProps = {
	positions: PositionRowItem[];
	openOrders: OpenOrder[];
	szDecimalsByCoin: Record<string, number>;
	onNavigate: (coin: string) => void;
	onCloseMarket: (coin: string, size: string, isLong: boolean) => void;
	onCloseLimitSubmit: (
		coin: string,
		size: string,
		isLong: boolean,
		price: string,
	) => void;
	onReverse: (coin: string, size: string, isLong: boolean) => void;
	onCloseAll: () => void;
	onSetTpsl: (params: {
		coin: string;
		isLong: boolean;
		tp: string | null;
		sl: string | null;
		size: string;
		markPrice: number;
		useLimitPrice: boolean;
	}) => void;
	onCancelOrder: (coin: string, oid: number) => void;
	isError?: boolean;
	onRetry?: () => void;
};

export function PositionsTable({
	positions,
	openOrders,
	szDecimalsByCoin,
	onNavigate,
	onCloseMarket,
	onCloseLimitSubmit,
	onReverse,
	onCloseAll,
	onSetTpsl,
	onCancelOrder,
	isError,
	onRetry,
}: PositionsTableProps) {
	const { t } = useTranslate();
	const columns = useMemo(() => createPositionsColumns(), []);

	const meta: PositionsTableMeta = {
		onNavigate,
		openOrders,
		szDecimalsByCoin,
		onCloseMarket,
		onCloseLimitSubmit,
		onReverse,
		onCloseAll,
		onSetTpsl,
		onCancelOrder,
	};

	const table = useReactTable({
		data: positions,
		columns,
		meta,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	return (
		<VirtualizedGridTable
			table={table}
			gridClass={POSITIONS_GRID_CLASS}
			estimateSize={44}
			emptyMessage={t("portfolio.no-positions")}
			isError={isError}
			onRetry={onRetry}
		/>
	);
}
